import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Users, Plus, Edit, UserX, Shield, Key, Trash2, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const roleLabels = {
    admin: 'Administrador',
    gestor: 'Gestor',
    operador: 'Operador',
    consulta: 'Consulta'
};

const UsersPage = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const { hasRole } = useAuth();

    const [formData, setFormData] = useState({
        nome: '',
        email: '',
        senha: '',
        role: 'operador'
    });

    useEffect(() => {
        fetchUsers();
    }, []);

    const fetchUsers = async () => {
        try {
            const response = await axios.get(`${API}/users`);
            setUsers(response.data);
        } catch (error) {
            toast.error('Erro ao carregar usuários');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            if (editingUser) {
                await axios.put(`${API}/users/${editingUser.id}`, {
                    nome: formData.nome,
                    role: formData.role
                });
                toast.success('Usuário atualizado!');
            } else {
                await axios.post(`${API}/users`, formData);
                toast.success('Usuário criado!');
            }
            
            setDialogOpen(false);
            resetForm();
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar usuário');
        }
    };

    const handleDeactivate = async (userId) => {
        if (!window.confirm('Deseja realmente desativar este usuário?')) return;
        
        try {
            await axios.delete(`${API}/users/${userId}`);
            toast.success('Usuário desativado!');
            fetchUsers();
        } catch (error) {
            toast.error('Erro ao desativar usuário');
        }
    };

    const handleActivate = async (userId) => {
        try {
            await axios.put(`${API}/users/${userId}`, { ativo: true });
            toast.success('Usuário ativado!');
            fetchUsers();
        } catch (error) {
            toast.error('Erro ao ativar usuário');
        }
    };

    const handleChangePassword = async () => {
        if (!newPassword || newPassword.length < 6) {
            toast.error('A senha deve ter pelo menos 6 caracteres');
            return;
        }
        
        try {
            await axios.put(`${API}/users/${selectedUser.id}/password`, { new_password: newPassword });
            toast.success('Senha alterada com sucesso!');
            setPasswordDialogOpen(false);
            setNewPassword('');
            setSelectedUser(null);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
        }
    };

    const handleDeletePermanent = async () => {
        try {
            await axios.delete(`${API}/users/${selectedUser.id}/permanent`);
            toast.success('Usuário excluído permanentemente!');
            setDeleteDialogOpen(false);
            setSelectedUser(null);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao excluir usuário');
        }
    };

    const openEditDialog = (user) => {
        setEditingUser(user);
        setFormData({
            nome: user.nome,
            email: user.email,
            senha: '',
            role: user.role
        });
        setDialogOpen(true);
    };

    const resetForm = () => {
        setFormData({
            nome: '',
            email: '',
            senha: '',
            role: 'operador'
        });
        setEditingUser(null);
    };

    const canManage = hasRole(['admin']);

    // Filtrar usuários com base na busca
    const filteredUsers = users.filter(user => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            user.nome?.toLowerCase().includes(term) ||
            user.email?.toLowerCase().includes(term) ||
            roleLabels[user.role]?.toLowerCase().includes(term)
        );
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="users-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Gestão de Usuários
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Controle de acessos e permissões
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                    {/* Campo de Busca */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar usuários..."
                            className="pl-10 pr-8 w-full sm:w-64 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                            data-testid="search-users"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                data-testid="clear-search-users"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                
                    {canManage && (
                        <Dialog open={dialogOpen} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) resetForm();
                        }}>
                            <DialogTrigger asChild>
                                <Button 
                                    data-testid="btn-new-user"
                                    className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Novo Usuário
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-lg">
                                <DialogHeader>
                                    <DialogTitle className="font-heading text-xl text-white">
                                        {editingUser ? 'Editar Usuário' : 'Novo Usuário'}
                                    </DialogTitle>
                                </DialogHeader>
                            
                                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Nome Completo
                                        </Label>
                                        <Input
                                            data-testid="input-user-nome"
                                            value={formData.nome}
                                            onChange={(e) => setFormData({...formData, nome: e.target.value})}
                                            required
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>

                                    {!editingUser && (
                                        <>
                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                E-mail
                                            </Label>
                                            <Input
                                                data-testid="input-user-email"
                                                type="email"
                                                value={formData.email}
                                                onChange={(e) => setFormData({...formData, email: e.target.value})}
                                                required
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                                Senha
                                            </Label>
                                            <Input
                                                data-testid="input-user-senha"
                                                type="password"
                                                value={formData.senha}
                                                onChange={(e) => setFormData({...formData, senha: e.target.value})}
                                                required={!editingUser}
                                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                            />
                                        </div>
                                    </>
                                )}

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Perfil
                                    </Label>
                                    <Select 
                                        value={formData.role} 
                                        onValueChange={(value) => setFormData({...formData, role: value})}
                                    >
                                        <SelectTrigger data-testid="select-user-role" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-slate-900 border-slate-800">
                                            {Object.entries(roleLabels).map(([key, label]) => (
                                                <SelectItem key={key} value={key}>{label}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setDialogOpen(false);
                                            resetForm();
                                        }}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-user"
                                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                    >
                                        {editingUser ? 'Atualizar' : 'Criar Usuário'}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
                </div>
            </div>

            {/* Users Table */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-0">
                    {filteredUsers.length === 0 ? (
                        <div className="p-12 text-center">
                            <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">
                                {searchTerm ? 'Nenhum usuário encontrado para a busca' : 'Nenhum usuário cadastrado'}
                            </p>
                        </div>
                    ) : (
                        <Table className="table-naval">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Nome</TableHead>
                                    <TableHead>E-mail</TableHead>
                                    <TableHead>Perfil</TableHead>
                                    <TableHead>Status</TableHead>
                                    {canManage && <TableHead>Ações</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredUsers.map((user) => (
                                    <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                                        <TableCell className="text-white font-medium">
                                            {user.nome}
                                        </TableCell>
                                        <TableCell className="text-slate-400">
                                            {user.email}
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex items-center gap-2">
                                                <Shield className={`w-4 h-4 ${
                                                    user.role === 'admin' ? 'text-red-400' :
                                                    user.role === 'gestor' ? 'text-amber-400' :
                                                    user.role === 'operador' ? 'text-cyan-400' : 'text-slate-400'
                                                }`} />
                                                <span className="text-slate-300">{roleLabels[user.role]}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${user.ativo ? 'badge-success' : 'badge-danger'}
                                            `}>
                                                {user.ativo ? 'Ativo' : 'Inativo'}
                                            </span>
                                        </TableCell>
                                        {canManage && (
                                            <TableCell>
                                                <div className="flex items-center gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => openEditDialog(user)}
                                                        className="text-slate-400 hover:text-white hover:bg-slate-800"
                                                        title="Editar usuário"
                                                    >
                                                        <Edit className="w-4 h-4" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            setSelectedUser(user);
                                                            setPasswordDialogOpen(true);
                                                        }}
                                                        className="text-amber-400 hover:text-amber-300 hover:bg-slate-800"
                                                        title="Alterar senha"
                                                        data-testid={`btn-change-password-${user.id}`}
                                                    >
                                                        <Key className="w-4 h-4" />
                                                    </Button>
                                                    {user.ativo ? (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleDeactivate(user.id)}
                                                            className="text-orange-400 hover:text-orange-300 hover:bg-slate-800"
                                                            title="Desativar usuário"
                                                        >
                                                            <UserX className="w-4 h-4" />
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleActivate(user.id)}
                                                            className="text-emerald-400 hover:text-emerald-300 hover:bg-slate-800"
                                                            title="Ativar usuário"
                                                        >
                                                            Ativar
                                                        </Button>
                                                    )}
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => {
                                                            setSelectedUser(user);
                                                            setDeleteDialogOpen(true);
                                                        }}
                                                        className="text-red-500 hover:text-red-400 hover:bg-slate-800"
                                                        title="Excluir permanentemente"
                                                        data-testid={`btn-delete-user-${user.id}`}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Role Descriptions */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardHeader className="border-b border-slate-800 p-4">
                    <CardTitle className="font-heading text-lg text-white">
                        Descrição dos Perfis
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="p-3 bg-slate-800/30 rounded-sm">
                        <div className="flex items-center gap-2 mb-2">
                            <Shield className="w-4 h-4 text-red-400" />
                            <span className="text-white font-medium">Administrador</span>
                        </div>
                        <p className="text-xs text-slate-400">
                            Acesso total. Gerencia usuários, configurações e todos os módulos.
                        </p>
                    </div>
                    <div className="p-3 bg-slate-800/30 rounded-sm">
                        <div className="flex items-center gap-2 mb-2">
                            <Shield className="w-4 h-4 text-amber-400" />
                            <span className="text-white font-medium">Gestor</span>
                        </div>
                        <p className="text-xs text-slate-400">
                            Valida registros, encerra ocorrências e acessa relatórios gerenciais.
                        </p>
                    </div>
                    <div className="p-3 bg-slate-800/30 rounded-sm">
                        <div className="flex items-center gap-2 mb-2">
                            <Shield className="w-4 h-4 text-cyan-400" />
                            <span className="text-white font-medium">Operador</span>
                        </div>
                        <p className="text-xs text-slate-400">
                            Registra dados operacionais, abre ROO, sinistros e preenche checklists.
                        </p>
                    </div>
                    <div className="p-3 bg-slate-800/30 rounded-sm">
                        <div className="flex items-center gap-2 mb-2">
                            <Shield className="w-4 h-4 text-slate-400" />
                            <span className="text-white font-medium">Consulta</span>
                        </div>
                        <p className="text-xs text-slate-400">
                            Acesso somente leitura. Pode visualizar e exportar relatórios.
                        </p>
                    </div>
                </CardContent>
            </Card>

            {/* Change Password Dialog */}
            <Dialog open={passwordDialogOpen} onOpenChange={(open) => {
                setPasswordDialogOpen(open);
                if (!open) {
                    setNewPassword('');
                    setSelectedUser(null);
                }
            }}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <Key className="w-5 h-5 text-amber-400" />
                            Alterar Senha
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        <p className="text-sm text-slate-400">
                            Alterando senha para: <span className="text-white font-medium">{selectedUser?.nome}</span>
                        </p>
                        <p className="text-xs text-slate-500">
                            E-mail: {selectedUser?.email}
                        </p>
                        
                        <div className="space-y-2">
                            <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                Nova Senha
                            </Label>
                            <Input
                                type="password"
                                data-testid="input-new-password"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                placeholder="Mínimo 6 caracteres"
                                className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                            />
                        </div>

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setPasswordDialogOpen(false);
                                    setNewPassword('');
                                    setSelectedUser(null);
                                }}
                                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleChangePassword}
                                data-testid="btn-confirm-password-change"
                                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-sm"
                            >
                                Alterar Senha
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialogOpen} onOpenChange={(open) => {
                setDeleteDialogOpen(open);
                if (!open) setSelectedUser(null);
            }}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <Trash2 className="w-5 h-5 text-red-500" />
                            Excluir Usuário Permanentemente
                        </DialogTitle>
                    </DialogHeader>
                    
                    <div className="space-y-4 mt-4">
                        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-sm">
                            <p className="text-sm text-red-400 font-medium mb-2">
                                Atenção: Esta ação é irreversível!
                            </p>
                            <p className="text-xs text-slate-400">
                                O usuário será removido permanentemente do sistema e todos os seus dados de acesso serão perdidos.
                            </p>
                        </div>
                        
                        <p className="text-sm text-slate-300">
                            Deseja realmente excluir o usuário <span className="text-white font-medium">{selectedUser?.nome}</span>?
                        </p>
                        <p className="text-xs text-slate-500">
                            E-mail: {selectedUser?.email}
                        </p>

                        <div className="flex gap-3 pt-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setDeleteDialogOpen(false);
                                    setSelectedUser(null);
                                }}
                                className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleDeletePermanent}
                                data-testid="btn-confirm-delete-user"
                                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-sm"
                            >
                                Excluir Permanentemente
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default UsersPage;
